import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import {
  Compass,
  Globe,
  Home,
  LayoutTemplate,
  Sparkles,
  Star,
  Trophy,
  DollarSign,
  Building2,
  Code2,
  BookOpen,
  Target,
} from 'lucide-react';
import {
  DEV_PORTS,
  SITE_BUILDER_URL,
  type SiteMapEntry,
  type SiteMapSection,
} from './siteMapShared';
import { DevServerControl } from '../components/siteMap/DevServerControl';
import { DevServersSummaryPanel } from '../components/siteMap/DevServersSummaryPanel';
import { resolveSiteMapDevServer, SECTION_DEV_SERVER, type DevServerId } from '../lib/devServersRegistry';

const PEAK = DEV_PORTS.peakPrioritiesSite;
const MARKETING = DEV_PORTS.marketingSite;

const atAGlanceStructure: SiteMapSection[] = [
  {
    category: 'Core',
    description: ':3150',
    pages: [
      { path: '/elevation/home', title: 'Elevation Home', description: '', icon: Home, color: 'primary' },
      { path: '/prioritization', title: 'Priorities.ai', description: '', icon: Trophy, color: 'primary' },
      { path: '/apps/mandate', title: 'Mandate', description: '', icon: Sparkles, color: 'plum' },
    ],
  },
  {
    category: 'Journey',
    description: ':3201 and the apps',
    pages: [
      { path: DEV_PORTS.whatsnext, title: "What's Next", description: '', icon: Compass, color: 'teal', external: true },
      { path: '/apps/first-100-days', title: 'First 100 Days', description: '', icon: Star, color: 'amber' },
    ],
  },
  {
    category: 'Peak Site',
    description: ':4322',
    pages: [
      { path: PEAK, title: 'Home', description: '', icon: Globe, color: 'primary', external: true },
      { path: `${PEAK}/platform`, title: 'Platform', description: '', icon: Target, color: 'sage', external: true },
      { path: `${PEAK}/method`, title: 'Method', description: '', icon: BookOpen, color: 'amber', external: true },
    ],
  },
  {
    category: 'Marketing',
    description: ':4321',
    pages: [
      { path: MARKETING, title: 'Home', description: '', icon: Globe, color: 'primary', external: true },
      { path: `${MARKETING}/platform`, title: 'Platform', description: '', icon: Target, color: 'sage', external: true },
      { path: `${MARKETING}/pricing`, title: 'Pricing', description: '', icon: DollarSign, color: 'amber', external: true },
      { path: `${MARKETING}/enterprise`, title: 'Enterprise', description: '', icon: Building2, color: 'plum', external: true },
      { path: `${MARKETING}/developers`, title: 'Developers', description: '', icon: Code2, color: 'charcoal', external: true },
    ],
  },
  {
    category: 'Publishing',
    description: ':3150 → :5174',
    pages: [
      {
        path: SITE_BUILDER_URL,
        title: 'Site Builder',
        description: '',
        icon: LayoutTemplate,
        color: 'primary',
        external: true,
        devServerId: 'siteBuilderDirect',
      },
    ],
  },
];

function displayPath(path: string): string {
  if (path.startsWith('http://localhost:')) {
    const port = path.replace('http://localhost:', '');
    return `:${port.split('/')[0]}`;
  }
  return path;
}

function CompactLink({ page }: { page: SiteMapEntry }) {
  const devServerId = resolveSiteMapDevServer(page);
  const label = (
    <span className="inline-flex items-center gap-1 min-w-0">
      <span className="truncate">{page.title}</span>
      {page.external && <ExternalLink className="h-3 w-3 shrink-0 opacity-40" />}
    </span>
  );
  const className =
    'group flex items-center justify-between gap-2 rounded px-1.5 py-0.5 text-sm text-charcoal-800 hover:bg-consulting-slate-blue-50 hover:text-consulting-slate-blue-800 transition-colors';

  const row = (
    <>
      {devServerId && <DevServerControl serverId={devServerId} />}
      {label}
      <span className="text-[10px] font-mono text-charcoal-400 shrink-0 group-hover:text-consulting-slate-blue-500">
        {displayPath(page.path)}
      </span>
    </>
  );

  if (page.external || page.path.startsWith('http')) {
    return (
      <a href={page.path} target="_blank" rel="noopener noreferrer" className={className} title={page.path}>
        {row}
      </a>
    );
  }

  return (
    <Link to={page.path} className={className} title={page.path}>
      {row}
    </Link>
  );
}

function CompactSection({
  section,
  sectionDevServerId,
}: {
  section: SiteMapSection;
  sectionDevServerId?: DevServerId;
}) {
  return (
    <section className="min-w-0 flex flex-col">
      <div className="mb-1 border-b border-charcoal-200 pb-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-xs font-bold uppercase tracking-wide text-charcoal-700">{section.category}</h2>
          <p className="text-[10px] text-charcoal-400 font-mono">{section.description}</p>
        </div>
        {sectionDevServerId && <DevServerControl serverId={sectionDevServerId} />}
      </div>
      <div className="flex flex-col gap-0.5">
        {section.pages.map((page) => (
          <CompactLink key={page.path} page={page} />
        ))}
      </div>
    </section>
  );
}

export function SiteMapAtAGlancePage() {
  const totalPages = atAGlanceStructure.reduce((total, section) => total + section.pages.length, 0);

  return (
    <div className="max-w-6xl mx-auto px-4 py-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to="/elevation/home"
            className="inline-flex items-center text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 shrink-0"
            title="Back to Elevation Home"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-base font-bold text-charcoal-900 truncate">Working Set</h1>
          <span className="text-xs text-charcoal-400 shrink-0">{totalPages} surfaces</span>
        </div>
        <Link
          to="/elevation/site-map"
          className="text-xs text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 shrink-0 whitespace-nowrap"
        >
          Full site map →
        </Link>
      </div>

      <DevServersSummaryPanel className="mb-3" compact />

      <div className="border border-charcoal-200 bg-[var(--bg-raised)] shadow-sm p-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-3">
          {atAGlanceStructure.map((section) => (
            <CompactSection
              key={section.category}
              section={section}
              sectionDevServerId={SECTION_DEV_SERVER[section.category]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
